<template>
  <div class="real-estate-dashboard">
    <!-- Sidebar KPIs -->
    <aside class="sidebar">
      <h3>{{ $t("dashboards.real-estate-owners.kpiTitle") }}</h3>
      <ul class="kpi-list">
        <li v-for="kpi in kpis" :key="kpi.id" :class="['kpi-item', { active: kpi.id === activeKpiId }]"
          @click="setActiveKpi(kpi.id)" @keyup.enter="setActiveKpi(kpi.id)" role="button" tabindex="0"
          :aria-selected="kpi.id === activeKpiId">
          <img class="kpi-icon" :src="kpi.id === activeKpiId ? kpi.icon.active : kpi.icon.inactive"
            :alt="kpi.name + ' icon'" />
          <span>{{ $t(kpi.name) }}</span>
        </li>
      </ul>
    </aside>

    <!-- Main Dashboard Content -->
    <main class="main-content">
      <!-- Energy Consumption View -->
      <div v-if="activeKpiId === 1" class="dashboard-view">
        <div class="widgets-row">
          <!-- Energy Intensity -->
          <div class="widget energy-intensity">
            <div class="widget-top">
              <h4>{{ $t("dashboards.real-estate-owners.energyIntensity") }}</h4>
              <img :src="icons.energy" class="ghost-icon" alt="" />
            </div>
            <div class="metric">
              <span class="value">{{ safeCurrentData.energy.intensity.toFixed(1) }}</span>
              <span class="unit">(kWh/sqm)</span>
            </div>
          </div>

          <!-- Change in Cost -->
          <div class="widget cost-change">
            <div class="widget-top">
              <span class="icon-wrap">
                <img :src="icons.costChange" alt="" />
              </span>
              <h4>{{ $t("dashboards.real-estate-owners.costChange") }}</h4>
            </div>
            <div class="metric"
              :class="safeCurrentData.energy.changePercent >= 0 ? 'change-positive' : 'change-negative'">
              <span class="icon">{{ safeCurrentData.energy.changePercent >= 0 ? '▲' : '▼' }}</span>
              <span class="value">{{ Math.abs(safeCurrentData.energy.changePercent || 0).toFixed(1) }}%</span>
              <span class="unit">{{
                safeCurrentData.energy.changePercent >= 0
                  ? $t("dashboards.real-estate-owners.costIncrease")
                  : $t("dashboards.real-estate-owners.costDecrease")
              }}</span>
            </div>
          </div>

          <!-- Cost Prediction -->
          <div class="widget cost-prediction">
            <div class="widget-top">
              <span class="icon-wrap">
                <img :src="icons.prediction" alt="" />
              </span>
              <h4>{{ $t("dashboards.real-estate-owners.costPrediction") }}</h4>
              <img :src="icons.detailPrediction" class="ghost-icon" alt="" />
            </div>
            <div class="metric">
              <span class="value">{{ formatCurrency(safeCurrentData.energy.nextMonthPrediction) }}</span>
              <span class="unit">{{ $t("dashboards.real-estate-owners.costPrice") }}</span>
            </div>
          </div>

          <!-- Activity Appliances -->
          <div class="widget activity-appliances">
            <div class="widget-top">
              <h4>{{ $t("dashboards.real-estate-owners.activityAppliancesTitle") }}</h4>
              <img :src="icons.activity" class="ghost-icon ghost-on-blue" alt="" />
            </div>
            <p class="appliance-text">
              {{ $t("dashboards.real-estate-owners.top3Appliances") }}
            </p>
            <div class="metric">
              <span class="value">{{ safeCurrentData.energy.topAppliancesShare.toFixed(1) }}%</span>
              <span class="unit">{{ $t("dashboards.real-estate-owners.ofTheNet") }}</span>
            </div>
          </div>
        </div>

        <div class="charts-row">
          <div class="chart-container energy-consumption">
            <div class="chart-header">
              <div class="title-section">
                <h3>{{ $t("dashboards.real-estate-owners.energyConsumption") }}</h3>
                <span class="unit">(kWh/sqm)</span>
              </div>
              <div class="benchmark">
                <span class="benchmark-label">{{ $t("dashboards.real-estate-owners.benchmark70") }}</span>
              </div>
            </div>
            <apexchart :options="energyConsumptionChart.options" :series="energyConsumptionChart.series" type="bar"
              height="300" />
            <div class="chart-legend">
              <div class="legend-indicator blue"></div>
              <span class="legend-text">{{ $t("dashboards.real-estate-owners.belowNorm") }}</span>
              <div class="legend-indicator red"></div>
              <span class="legend-text">{{ $t("dashboards.real-estate-owners.aboveNorm") }}</span>
            </div>

          </div>

          <div class="chart-container active-spaces">
            <h3>{{ $t("dashboards.real-estate-owners.activeSpaces") }}</h3>
            <div class="donut-container">
              <apexchart :options="activeSpacesChart.options" :series="activeSpacesChart.series" type="donut"
                height="300" />
              <div class="center-label">
                <span class="center-text">{{ $t("dashboards.real-estate-owners.officeLabel") }}</span>
              </div>
            </div>
            <div class="donut-legend">
              <div class="legend-item">
                <span class="color-box" style="background: #4299e1"></span>
                <span>{{ $t("dashboards.real-estate-owners.openOfficeLabel") }}
                  {{ safeCurrentData.energy.activeSpaces.openOffice }}%</span>
              </div>
              <div class="legend-item">
                <span class="color-box" style="background: #48bb78"></span>
                <span>{{ $t("dashboards.real-estate-owners.meetingRoomShare") }}
                  {{ safeCurrentData.energy.activeSpaces.meetingRoom }}%</span>
              </div>
              <div class="legend-item">
                <span class="color-box" style="background: #ed8936"></span>
                <span>{{ $t("dashboards.real-estate-owners.lobbyShare") }}
                  {{ safeCurrentData.energy.activeSpaces.lobby }}%</span>
              </div>
              <div class="legend-item">
                <span class="color-box" style="background: #eab308"></span>
                <span>{{ $t("dashboards.renters.conferenceRoom") }}
                  {{ safeCurrentData.energy.activeSpaces.conferenceRoom }}%</span>
              </div>
            </div>
            <p class="chart-description">
              {{ $t("dashboards.real-estate-owners.esgComparison") }}
            </p>
          </div>
        </div>

        <div class="usage-estimate">
          <h3>{{ $t("dashboards.real-estate-owners.usageEstimate") }}</h3>
          <apexchart :options="usageEstimateChart.options" :series="usageEstimateChart.series" type="bar"
            height="300" />
        </div>
      </div>

      <!-- Carbon Emissions View -->
      <div v-else-if="activeKpiId === 2" class="dashboard-view carbon-emissions-view">
        <div class="main-charts-grid">
          <div class="chart-card total-emissions">
            <div class="te-heading">
              <div class="te-title">
                <h3>{{ $t("dashboards.real-estate-owners.totalEmissions") }}</h3>
                <p class="subtitle">(CO₂)</p>
              </div>
              <span class="benchmark-label pill">{{ $t("dashboards.real-estate-owners.benchmark70") }}</span>
            </div>

            <div class="emissions-breakdown">
  <div
    class="emissions-item"
    v-for="f in safeCurrentData?.carbon?.floorBreakdown ?? []"
    :key="f.floor"
  >
    <span class="floor-indicator" :class="getFloorIndicatorClass(f.floor)"></span>
    <span class="floor-label">{{ getFloorLabel(f.floor) }}</span>

    <span class="percentage">{{ toFixed2(f.percentage) }}%</span>

    <span class="change" :class="(f.changePercent ?? 0) >= 0 ? 'positive' : 'negative'">
      ({{ (f.changePercent ?? 0) >= 0 ? '+' : '' }}{{ toFixed2(f.changePercent) }}%)
    </span>
  </div>
</div>


            <div class="pie-chart-container">
              <apexchart :options="totalEmissionsChart.options" :series="totalEmissionsChart.series" type="pie"
                height="280" />
            </div>
          </div>

          <div class="chart-card emissions-gauge">
            <h3>{{ $t("dashboards.real-estate-owners.activeSpaces") }}</h3>
            <p class="subtitle">(CO₂)</p>
            <div class="gauge-container">
              <apexchart :options="emissionsGaugeChart.options" :series="emissionsGaugeChart.series" type="radialBar"
                height="280" />
            </div>
            <div class="benchmark-label bottom">
              {{ $t("dashboards.real-estate-owners.emissionBenchmark") }}
            </div>
          </div>
        </div>

        <div class="chart-card">
          <h3>{{ $t("dashboards.real-estate-owners.emissionsOverTime") }}</h3>
          <div>
            <apexchart :options="emissionsTimelineChart.options" :series="emissionsTimelineChart.series" type="line"
              height="350" />
          </div>
        </div>
      </div>

      <!-- ESG Score View -->
      <div v-else-if="activeKpiId === 3" class="dashboard-view esg-view">
        <div class="esg-grid">
          <div class="chart-card esg-benchmarking">
            <h3>{{ $t("dashboards.real-estate-owners.esgTools") }}</h3>
            <p class="subtitle">{{ $t("dashboards.real-estate-owners.esgToolsSubtitle") }}</p>
            <div class="benchmark-label">{{ $t("dashboards.real-estate-owners.benchmark70") }}</div>

            <div class="esg-gauge-wrap" @mousedown="startDrag" @touchstart.prevent="startDrag" @mousemove="onDrag"
              @touchmove.prevent="onDrag" @mouseup="stopDrag" @mouseleave="stopDrag" @touchend="stopDrag">
              <svg class="esg-gauge" viewBox="-130 -130 260 240" preserveAspectRatio="xMidYMid meet"
                aria-label="ESG Benchmark Gauge" ref="gaugeSvg">
                <path :d="trackPath" class="gauge-track" />
                <path :d="progressPath" class="gauge-progress" />
                <g>
                  <template v-for="(s, i) in esgSegments" :key="i">
                    <path :d="s.d" :class="['gauge-segment', s.side, s.active ? 'active' : '']" />
                  </template>
                </g>
                <g :transform="'rotate(' + esgNeedleAngle + ')'">
                  <polygon points="0,-6 0,6 74,0" class="needle" />
                  <circle cx="0" cy="0" r="6" class="needle-hole" />
                </g>
                <text :x="label0.x" :y="label0.y" class="tick-label">0</text>
                <text :x="labelMid.x" :y="labelMid.y" class="tick-label mid">
                  {{ esgGaugeValue.toFixed(1) }}
                </text>
                <text :x="label160.x" :y="label160.y" dx="6" class="tick-label">160.0</text>
                <text :x="captionPos.x" :y="captionPos.y" class="score-caption">
                  {{ $t("dashboards.real-estate-owners.esgOverallScore") }}
                </text>
              </svg>

              <p class="esg-description">
                {{ esgGaugeValue.toFixed(1) }} - {{ $t("dashboards.real-estate-owners.esgToolsDesc") }}
              </p>
            </div>
          </div>

          <div class="chart-card esg-score">
            <h3>{{ $t("dashboards.real-estate-owners.esgScore") }}</h3>
            <p class="subtitle">{{ $t("dashboards.real-estate-owners.esgComplianceDec") }}</p>
            <div class="esg-donut">
              <apexchart :options="esgScoreChart.options" :series="esgScoreChart.series" type="donut" height="300" />
            </div>

            <div class="donut-legend">
              <div class="legend-item">
                <span class="check-icon">✓</span>
                <span>{{ $t("dashboards.real-estate-owners.co2Reporting") }}</span>
              </div>
              <div class="legend-item">
                <span class="color-box" style="background: #3f5baa"></span>
                <span>{{ $t("dashboards.real-estate-owners.lobbyShare") }}
                  {{ safeCurrentData.esg.spaceAllocation.lobby }}%</span>
              </div>
              <div class="legend-item">
                <span class="check-icon">✓</span>
                <span>{{ $t("dashboards.real-estate-owners.energyTracking") }}</span>
              </div>
              <div class="legend-item">
                <span class="color-box" style="background: #54ca54"></span>
                <span>{{ $t("dashboards.renters.conferenceRoom") }}
                  {{ safeCurrentData.esg.spaceAllocation.conferenceRoom }}%</span>
              </div>
              <div class="legend-item">
                <span class="check-icon">✓</span>
                <span>{{ $t("dashboards.real-estate-owners.gresbIntegration") }}</span>
              </div>
              <div class="legend-item">
                <span class="color-box" style="background: #ff9114"></span>
                <span>{{ $t("dashboards.real-estate-owners.mRooms") }}
                  {{ safeCurrentData.esg.spaceAllocation.meetingRoom }}%</span>
              </div>
              <div class="legend-item">
                <span class="check-icon">✓</span>
                <span>{{ $t("dashboards.real-estate-owners.iso14001Compliance") }}</span>
              </div>
              <div class="legend-item">
                <span class="color-box" style="background: #fdd433"></span>
                <span>{{ $t("dashboards.real-estate-owners.openOffice") }}
                  {{ safeCurrentData.esg.spaceAllocation.openOffice }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Occupancy Rate View -->
      <div v-else-if="activeKpiId === 4" class="dashboard-view occupancy-view">
        <div class="occupancy-grid">
          <div class="chart-card occupancy-rate">
            <h3>{{ $t("dashboards.real-estate-owners.occupancyRate") }}</h3>
            <div class="occupancy-donut">
              <apexchart :options="occupancyRateChart.options" :series="occupancyRateChart.series" type="donut"
                height="300" />
            </div>
            <div class="occupancy-legend">
              <div class="legend-item">
                <span class="indicator occupied"></span>
                <span>{{ $t("dashboards.real-estate-owners.occupancyStatusOccupied") }}</span>
              </div>
              <div class="legend-item">
                <span class="indicator free"></span>
                <span>{{ $t("dashboards.real-estate-owners.occupancyStatusFreeUnused") }}</span>
              </div>
            </div>
          </div>

          <div class="chart-card unified-dashboard">
            <h3>{{ $t("dashboards.real-estate-owners.floorplanOccupancy") }}</h3>
            <div class="floor-plan">
              <div class="floor-plan-image">
                <img :src="unifiedDashboard" alt="Unified Dashboard" />
              </div>
              <div class="room-legend">
                <div class="legend-item">
                  <span class="room-indicator blue"></span>
                  <span>{{ $t("dashboards.real-estate-owners.floorOpenOffice") }}</span>
                </div>
                <div class="legend-item">
                  <span class="room-indicator green"></span>
                  <span>{{ $t("dashboards.real-estate-owners.floorMeetingRoomLabel") }}</span>
                </div>
                <div class="legend-item">
                  <span class="room-indicator orange"></span>
                  <span>{{ $t("dashboards.real-estate-owners.floorLobbyLabel") }}</span>
                </div>
                <div class="legend-item">
                  <span class="room-indicator yellow"></span>
                  <span>{{ $t("dashboards.real-estate-owners.floorConferenceRoomsLabel") }}</span>
                </div>
              </div>
              <div>
                <label>{{ $t("dashboards.real-estate-owners.spaceUtilization") }}</label>
                <div class="utilization-slider">
                  <div class="slider-track">
                    <div class="slider-fill" :style="{ width: safeCurrentData.occupancy.spaceUtilization + '%' }"></div>
                    <div class="slider-thumb" :style="{ left: safeCurrentData.occupancy.spaceUtilization + '%' }">
                      <span class="thumb-tooltip">{{ safeCurrentData.occupancy.spaceUtilization }}%</span>
                    </div>
                  </div>
                  <div class="slider-labels">
                    <span>%0</span>
                    <span>%100</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Operational Cost View -->
      <div v-else-if="activeKpiId === 5" class="dashboard-view operational-cost-view">
        <div class="cost-metrics-row">
          <div class="metric-card">
            <div class="circular-progress-wrapper">
              <svg class="circular-progress" viewBox="0 0 120 120">
                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)" stroke-width="10"
                  fill="none">
                </circle>
                <circle class="progress-circle orange" cx="60" cy="60" r="50" :stroke-dasharray="circumference"
                  :stroke-dashoffset="getOffset(safeCurrentData.operational.energyAnalytics)" stroke-width="10"
                  fill="none">
                </circle>
              </svg>
              <div class="progress-value">
                <span class="percentage">{{ safeCurrentData.operational.energyAnalytics }}%</span>
              </div>
            </div>
            <div class="metric-info">
              <h5>{{ $t("dashboards.real-estate-owners.energyAnalytics") }}</h5>
              <p class="kpi-description">
                {{ $t("dashboards.real-estate-owners.energyAnalyticsDesc") }}
              </p>
            </div>
          </div>

          <div class="metric-card">
            <div class="circular-progress-wrapper">
              <svg class="circular-progress" viewBox="0 0 120 120">
                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)" stroke-width="10"
                  fill="none">
                </circle>
                <circle class="progress-circle blue" cx="60" cy="60" r="50" :stroke-dasharray="circumference"
                  :stroke-dashoffset="getOffset(safeCurrentData.operational.dynamicOccupancy)" stroke-width="10"
                  fill="none">
                </circle>
              </svg>
              <div class="progress-value">
                <span class="percentage">{{ safeCurrentData.operational.dynamicOccupancy }}%</span>
              </div>
            </div>
            <div class="metric-info">
              <h5>{{ $t("dashboards.real-estate-owners.dynamicOccupancy") }}</h5>
              <p class="kpi-description">
                {{ $t("dashboards.real-estate-owners.dynamicOccupancyDesc") }}
              </p>
            </div>
          </div>

          <div class="metric-card">
            <div class="circular-progress-wrapper">
              <svg class="circular-progress" viewBox="0 0 120 120">
                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)" stroke-width="10"
                  fill="none">
                </circle>
                <circle class="progress-circle green" cx="60" cy="60" r="50" :stroke-dasharray="circumference"
                  :stroke-dashoffset="getOffset(safeCurrentData.operational.utilizationAnalytics)" stroke-width="10"
                  fill="none"></circle>
              </svg>
              <div class="progress-value">
                <span class="percentage">{{ safeCurrentData.operational.utilizationAnalytics }}%</span>
              </div>
            </div>
            <div class="metric-info">
              <h5>{{ $t("dashboards.real-estate-owners.utilizationAnalytics") }}</h5>
              <p class="kpi-description">
                {{ $t("dashboards.real-estate-owners.utilizationAnalyticsDesc") }}
              </p>
            </div>
          </div>
        </div>

        <div class="cost-analysis-row">
          <div class="chart-card space-efficiency">
            <div class="card-header">
              <h3>{{ $t("dashboards.real-estate-owners.optimizeEfficiency") }}</h3>
            </div>
            <div class="efficiency-donut">
              <apexchart :options="spaceEfficiencyChart.options" :series="spaceEfficiencyChart.series" type="donut"
                height="300" />
            </div>
            <div class="efficiency-legend">
              <div class="legend-item">
                <span class="indicator" style="background: #3b82f6"></span>
                <span>{{ $t("dashboards.real-estate-owners.openOfficeLabel") }}
                  ({{ safeCurrentData.operational.spaceEfficiency.openOffice }}%)</span>
              </div>
              <div class="legend-item">
                <span class="indicator" style="background: #10b981"></span>
                <span>{{ $t("dashboards.real-estate-owners.meetingRoomShare") }}
                  ({{ safeCurrentData.operational.spaceEfficiency.meetingRoom }}%)</span>
              </div>
              <div class="legend-item">
                <span class="indicator" style="background: #f97316"></span>
                <span>{{ $t("dashboards.real-estate-owners.lobbyShare") }}
                  ({{ safeCurrentData.operational.spaceEfficiency.lobby }}%)</span>
              </div>
              <div class="legend-item">
                <span class="indicator" style="background: #fdd433"></span>
                <span>{{ $t("dashboards.real-estate-owners.conferenceRoomsLabel") }}
                  ({{ safeCurrentData.operational.spaceEfficiency.conferenceRoom }}%)</span>
              </div>
            </div>
            <p class="chart-description">
              {{ $t("dashboards.real-estate-owners.trackUsage") }}
            </p>
          </div>

          <div class="chart-card cost-per-sqm">
            <h3>{{ $t("dashboards.real-estate-owners.costPerSqm") }}</h3>
            <div class="building-visualization">
              <div class="building-container">
                <div class="cost-bubbles">
                  <div class="cost-bubble blue">€{{ safeCurrentData.operational.costPerSqm.blue }}/sqm</div>
                  <div class="cost-bubble red">€{{ safeCurrentData.operational.costPerSqm.red }}/sqm</div>
                  <div class="cost-bubble yellow">€{{ safeCurrentData.operational.costPerSqm.yellow }}/sqm</div>
                </div>
                <div class="building-image">
                  <img :src="costPerSqm" alt="Cost Per sqm" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { getRealEstateData } from "./realEstateOwnersData";

// Assets
import costPerSqm from "@/assets/client-segments/cost-per-sqm.png";
import unifiedDashboard from "@/assets/client-segments/unified-dashboard.png";
import energy from "@/assets/client-segments/energy-intensity.svg";
import prediction from "@/assets/client-segments/cost-prediction.svg";
import detailPrediction from "@/assets/client-segments/detail-cost-prediction.svg";
import activity from "@/assets/client-segments/detail-activity-appliances.svg";
import costChange from "@/assets/client-segments/change-in-cost.svg";

// KPI Icons
import carbonDioxideActive from "@/assets/client-segments/kpis-icons/carbon-dioxide-active.svg";
import carbonDioxideInactive from "@/assets/client-segments/kpis-icons/carbon-dioxide-inactive.svg";
import chartColumnActive from "@/assets/client-segments/kpis-icons/chart-column-active.svg";
import chartColumnInactive from "@/assets/client-segments/kpis-icons/chart-column-inactive.svg";
import energyConsumptionActive from "@/assets/client-segments/kpis-icons/energy-consumption-active.svg";
import energyConsumptionInactive from "@/assets/client-segments/kpis-icons/energy-consumption-inactive.svg";
import handCoinsActive from "@/assets/client-segments/kpis-icons/hand-coins-active.svg";
import handCoinsInactive from "@/assets/client-segments/kpis-icons/hand-coins-inactive.svg";
import leafActive from "@/assets/client-segments/kpis-icons/leaf-active.svg";
import leafInactive from "@/assets/client-segments/kpis-icons/leaf-inactive.svg";

const icons = { energy, prediction, activity, detailPrediction, costChange };
const { t, te } = useI18n()

const props = defineProps({
  screenWidth: { type: Number, default: window.innerWidth }
});

// State
const activeKpiId = ref(1);
const monthlyData = ref([]);
const currentData = ref(null);
const isDataLoaded = ref(false);
const circumference = 2 * Math.PI * 50;
const benchmarkValue = 70;

// Computed
const isMobile = computed(() => props.screenWidth <= 768);
const isTablet = computed(() => props.screenWidth > 768 && props.screenWidth <= 1200);

// KPIs Configuration
const kpis = ref([
  { id: 1, name: "dashboards.real-estate-owners.energyConsumption", icon: { active: energyConsumptionActive, inactive: energyConsumptionInactive } },
  { id: 2, name: "dashboards.real-estate-owners.carbonEmissions", icon: { active: carbonDioxideActive, inactive: carbonDioxideInactive } },
  { id: 3, name: "dashboards.real-estate-owners.esgScore", icon: { active: leafActive, inactive: leafInactive } },
  { id: 4, name: "dashboards.real-estate-owners.occupancyRate", icon: { active: chartColumnActive, inactive: chartColumnInactive } },
  { id: 5, name: "dashboards.real-estate-owners.operationalCost", icon: { active: handCoinsActive, inactive: handCoinsInactive } }
]);

const EMISSIONS_PALETTE = ["#3b82f6", "#7dd3fc", "#a78bfa", "#1e40af"];

const getFloorColor = (n) => EMISSIONS_PALETTE[(Number(n) - 1) % EMISSIONS_PALETTE.length];

// Safe Current Data
const safeCurrentData = computed(() => {
  if (!currentData.value || !isDataLoaded.value) {
    // Return a default/empty data structure
    return {
      energy: {
        intensity: 0,
        changePercent: 0,
        totalCost: 0,
        nextMonthPrediction: 0,
        topAppliancesShare: 0,
        comparison: { previous: 0, current: 0 },
        activeSpaces: { openOffice: 0, meetingRoom: 0, lobby: 0, conferenceRoom: 0 },
        hourlyUsage: {
          hours: Array(12).fill(""),
          electricity: Array(12).fill(0),
          gas: Array(12).fill(0)
        }
      },
      carbon: {
        totalEmissions: 0,
        changePercent: 0,
        floorBreakdown: [
          { floor: 1, percentage: 0, changePercent: 0, emissions: 0 },
          { floor: 2, percentage: 0, changePercent: 0, emissions: 0 },
          { floor: 3, percentage: 0, changePercent: 0, emissions: 0 },
          { floor: 4, percentage: 0, changePercent: 0, emissions: 0 }
        ],
        gaugeValue: 0,
        timeline: { current: 0, projected: 0 }
      },
      esg: {
        overallScore: 0,
        changePercent: 0,
        gaugeValue: 0,
        spaceAllocation: { lobby: 0, conferenceRoom: 0, meetingRoom: 0, openOffice: 0 },
        compliance: { co2Reporting: false, energyTracking: false, gresbIntegration: false, iso14001: false }
      },
      occupancy: {
        rate: 0,
        changePercent: 0,
        peopleOnsite: 0,
        totalCapacity: 200,
        donutData: { occupied: 0, free: 0 },
        floorplanBreakdown: { openOffice: 0, meetingRoom: 0, lobby: 0, conferenceRoom: 0 },
        spaceUtilization: 0
      },
      operational: {
        energyAnalytics: 0,
        changePercent: 0,
        dynamicOccupancy: 0,
        utilizationAnalytics: 0,
        spaceEfficiency: { openOffice: 0, meetingRoom: 0, lobby: 0, conferenceRoom: 0 },
        costPerSqm: { blue: 0, red: 0, yellow: 0 }
      }
    };
  }
  return currentData.value;
});

// Methods
function setActiveKpi(kpiId) {
  activeKpiId.value = kpiId;
}

function getOffset(percentage) {
  return circumference - (percentage / 100) * circumference;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(value);
}

function getFloorIndicatorClass(floorNumber) {
  // Normalize input to string and number to safely handle both formats
  if (floorNumber == null) return '';

  const str = String(floorNumber).trim();
  // If it's an exact numeric floor like "1" or 2, prefer exact match
  const num = parseInt(str, 10);
  const classesByNum = { 1: 'blue', 2: 'light-blue', 3: 'purple', 4: 'dark-blue' };
  if (!Number.isNaN(num) && classesByNum[num]) {
    return classesByNum[num];
  }

  // Fallback: check if the string contains a floor digit (handles values like "Floor 1" or "1A")
  if (str.includes('1')) return 'blue';
  if (str.includes('2')) return 'light-blue';
  if (str.includes('3')) return 'purple';
  if (str.includes('4')) return 'dark-blue';

  return '';
}
const getFloorLabel = (n) => {
  const keyPreferred = `dashboards.real-estate-owners.floor${n}`
  if (te(keyPreferred)) return t(keyPreferred)

  // fallback for digit-first keys; must use bracket notation
  const keyAlt = `dashboards["real-estate-owners"]["${n}floor"]`
  if (te(keyAlt)) return t(keyAlt)

  // final fallback
  return `${n}`
}

const toFixed2 = (val) => Number(val ?? 0).toFixed(2)

// Initialize Data
function initializeData() {
  try {
    // Get data with automatic interpolation
    const realEstateData = getRealEstateData();
    monthlyData.value = realEstateData.monthlyData;
    currentData.value = monthlyData.value[monthlyData.value.length - 1];
    isDataLoaded.value = true;
  } catch (error) {
    console.error("Error initializing data:", error);
    isDataLoaded.value = false;
  }
}

// Energy Consumption Chart
const energyConsumptionChart = computed(() => ({
  series: [{
    name: t("dashboards.real-estate-owners.energy"),
    data: [safeCurrentData.value.energy.comparison.previous, safeCurrentData.value.energy.comparison.current]
  }],
  options: {
    chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "40%",
        borderRadius: 8,
        distributed: false,
        colors: {
          ranges: [
            { from: 0, to: benchmarkValue - 0.01, color: "#3C82F5" },
            { from: benchmarkValue, to: 9999, color: "#ef4444" }
          ]
        }
      }
    },
    dataLabels: { enabled: false },

    xaxis: {
      categories: [t("dashboards.real-estate-owners.previousPeriod"), t("dashboards.real-estate-owners.currentPeriod")],
      labels: { style: { colors: "#94a3b8", fontSize: "13px" } }
    },
    yaxis: { max: 100, labels: { style: { colors: "#94a3b8", fontSize: "13px" } } },
    grid: { borderColor: "#374151", strokeDashArray: 3 },
    legend: { show: false },

    annotations: {
      yaxis: [{
        y: benchmarkValue,
        borderColor: "#10b981",
        label: { borderColor: "#10b981", style: { fontSize: "13px", color: "#fff", background: "#10b981" }, text: `Benchmark: ${benchmarkValue}` }
      }]
    }
  }
}));

// Active Spaces Chart
const activeSpacesChart = computed(() => ({
  series: [
    safeCurrentData.value.energy.activeSpaces.openOffice,
    safeCurrentData.value.energy.activeSpaces.meetingRoom,
    safeCurrentData.value.energy.activeSpaces.lobby,
    safeCurrentData.value.energy.activeSpaces.conferenceRoom
  ],
  options: {
    chart: { type: "donut", background: "transparent" },
    colors: ["#3b82f6", "#10b981", "#f97316", "#eab308"],
    labels: [
      t("dashboards.real-estate-owners.openOffice"),
      t("dashboards.real-estate-owners.meetingRoomLabel"),
      t("dashboards.real-estate-owners.lobbyLabel"),
      t("dashboards.real-estate-owners.roomsLabel")
    ],
    legend: { show: false },
    plotOptions: { pie: { donut: { size: "65%", labels: { show: true, name: { show: false }, value: { show: false } } } } },
    dataLabels: { enabled: false },
    stroke: { show: false }
  }
}));

// Usage Estimate Chart
const usageEstimateChart = computed(() => ({
  series: [
    { name: t("dashboards.real-estate-owners.electricity"), data: safeCurrentData.value.energy.hourlyUsage.electricity },
    { name: t("dashboards.real-estate-owners.gas"), data: safeCurrentData.value.energy.hourlyUsage.gas }
  ],
  options: {
    chart: { type: "bar", stacked: true, toolbar: { show: false }, background: "transparent", animations: { enabled: true, easing: "easeinout", speed: 1000 } },
    colors: ["#06b6d4", "#f97316"],
    plotOptions: { bar: { horizontal: false, columnWidth: "70%", borderRadius: 2 } },
    xaxis: {
      categories: safeCurrentData.value.energy.hourlyUsage.hours,
      labels: { style: { colors: "#94a3b8", fontSize: "12px" } }
    },
    yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "12px" } } },
    legend: { position: "bottom", labels: { colors: "#94a3b8" } },
    grid: { borderColor: "#374151", strokeDashArray: 3, padding: { bottom: 20 } },
    dataLabels: { enabled: false }
  }
}));

// Total Emissions Chart
const totalEmissionsChart = computed(() => ({
  series: safeCurrentData.value.carbon.floorBreakdown.map(f => f.percentage),
  options: {
    chart: {
      type: "pie",
      background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 1200, animateGradually: { enabled: true, delay: 120 }, dynamicAnimation: { enabled: true, speed: 600 } },
      dropShadow: { enabled: true, top: 2, left: 2, blur: 10, opacity: 0.25, color: "#000" }
    },
    colors: ["#3b82f6", "#7dd3fc", "#a78bfa", "#1e40af"],
   labels: [
  t('dashboards.real-estate-owners.floor1'),
  t('dashboards.real-estate-owners.floor2'),
  t('dashboards.real-estate-owners.floor3'),
  t('dashboards.real-estate-owners.floor4'),
],
    legend: { show: false },
    dataLabels: {
      enabled: true,
      style: { fontSize: "13px", fontFamily: "inherit", fontWeight: 700, colors: ["#ffffff"] },
      offset: 0,
      formatter: (val) => `${Math.round(val)}%`,
      dropShadow: { enabled: true, top: 1, left: 1, blur: 2, color: "#000", opacity: 0.65 }
    },
    stroke: { show: true, width: 2, colors: ["rgba(0,0,0,0.25)"] },
    plotOptions: { pie: { expandOnClick: false, dataLabels: { offset: -6 }, donut: { size: "0%" } } },
    tooltip: { enabled: true, theme: "dark", y: { formatter: (v) => `${v.toFixed(2)}%` } }
  }
}));

// Emissions Gauge Chart
const emissionsGaugeChart = computed(() => ({
  series: [safeCurrentData.value.carbon.gaugeValue],
  options: {
    chart: {
      type: "radialBar",
      background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 2000, animateGradually: { enabled: true, delay: 300 }, dynamicAnimation: { enabled: true, speed: 1000 } },
      dropShadow: { enabled: true, top: 0, left: 0, blur: 15, opacity: 0.3, color: "#ef4444" }
    },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: { size: "55%", background: "transparent", dropShadow: { enabled: true, top: 3, left: 0, blur: 4, opacity: 0.24 } },
        track: { background: "rgba(55, 65, 81, 0.15)", strokeWidth: "20px", margin: 8, dropShadow: { enabled: true, top: -3, left: 0, blur: 4, opacity: 0.35 } },
        dataLabels: {
          show: true,
          name: { offsetY: -15, show: true, color: "#94a3b8", fontSize: "14px", fontWeight: 500 },
          value: { offsetY: 10, color: "#ffffff", fontSize: "42px", fontWeight: 700, show: true, formatter: (val) => Math.round(val) + "%" }
        }
      }
    },
    colors: ["#ef4444"],
    stroke: { lineCap: "round", curve: "smooth" },
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "diagonal1",
        shadeIntensity: 0.3,
        gradientToColors: ["#22c55e", "#10b981"],
        stops: [0, 45, 80, 100],
        colorStops: [
          { offset: 0, color: "#ef4444", opacity: 1 },
          { offset: 45, color: "#f59e0b", opacity: 0.95 },
          { offset: 80, color: "#22c55e", opacity: 0.9 },
          { offset: 100, color: "#10b981", opacity: 0.85 }
        ]
      }
    },
    labels: ["Emissions"],
    legend: { show: false }
  }
}));

// Emissions Timeline Chart
const emissionsTimelineChart = computed(() => {
  const last12Months = monthlyData.value.slice(-12);
  if (last12Months.length === 0) {
    return {
      series: [
        { name: t("dashboards.real-estate-owners.currentLabel"), data: [] },
        { name: t("dashboards.real-estate-owners.projectedLabel"), data: [] }
      ],
      options: {
        chart: { type: "line", toolbar: { show: false }, background: "transparent" },
        xaxis: { categories: [] }
      }
    };
  }

  return {
    series: [
      { name: t("dashboards.real-estate-owners.currentLabel"), data: last12Months.map(m => m.carbon.timeline.current) },
      { name: t("dashboards.real-estate-owners.projectedLabel"), data: last12Months.map(m => m.carbon.timeline.projected) }
    ],
    options: {
      chart: { type: "line", toolbar: { show: false }, background: "transparent", animations: { enabled: true, easing: "easeinout", speed: 1000 }, height: 420 },
      colors: ["#3b82f6", "#f59e0b"],
      stroke: { curve: "smooth", width: 4, dashArray: [0, 8] },
      grid: { borderColor: "#374151", strokeDashArray: 4, padding: { bottom: 10 } },
      xaxis: {
        categories: last12Months.map(m => m.displayMonth),
        labels: { style: { colors: "#94a3b8", fontSize: "13px" } },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: { min: 0, max: 1800, tickAmount: 8, labels: { style: { colors: "#94a3b8", fontSize: "13px" }, formatter: (val) => Math.round(val) } },
      legend: { show: true, position: "top", horizontalAlign: "right", floating: true, offsetY: -20, offsetX: -10, labels: { colors: "#d1d5db", fontSize: "15px" } },
      dataLabels: { enabled: false },
      tooltip: { theme: "dark", shared: true, intersect: false, y: { formatter: (val) => val + " kg CO₂" } },
      markers: { size: 0, hover: { size: 6 } }
    }
  };
});

// ESG Gauge
const esgGaugeMin = 0;
const esgGaugeMax = 160;
const esgStartDeg = -180;
const esgEndDeg = 0;
const outerR = 96;
const innerR = 78;
const segCount = 32;
const segGap = 0.14;
const trackR = 60;

const esgGaugeValue = ref(80);
const esgValueAnimated = ref(0);
const gaugeSvg = ref(null);
let dragging = false;

function tweenValue(targetRef, to, dur = 1500) {
  const from = targetRef.value;
  const start = performance.now();
  function step(t) {
    const p = Math.min(1, (t - start) / dur);
    targetRef.value = from + ((to - from) * (1 - Math.cos(p * Math.PI))) / 2;
    if (p < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

watch(esgGaugeValue, (to) => tweenValue(esgValueAnimated, to));

const toRad = (d) => (Math.PI / 180) * d;
const angleFor = (v) => esgStartDeg + ((v - esgGaugeMin) * (esgEndDeg - esgStartDeg)) / (esgGaugeMax - esgGaugeMin);

function ringSector(R, r, a0, a1) {
  const sx = R * Math.cos(toRad(a0)), sy = R * Math.sin(toRad(a0));
  const ex = R * Math.cos(toRad(a1)), ey = R * Math.sin(toRad(a1));
  const sxi = r * Math.cos(toRad(a1)), syi = r * Math.sin(toRad(a1));
  const exi = r * Math.cos(toRad(a0)), eyi = r * Math.sin(toRad(a0));
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return `M ${sx} ${sy} A ${R} ${R} 0 ${large} 1 ${ex} ${ey} L ${sxi} ${syi} A ${r} ${r} 0 ${large} 0 ${exi} ${eyi} Z`;
}

function arcPath(r, a0, a1) {
  const sx = r * Math.cos(toRad(a0)), sy = r * Math.sin(toRad(a0));
  const ex = r * Math.cos(toRad(a1)), ey = r * Math.sin(toRad(a1));
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
}

const segSpan = (esgEndDeg - esgStartDeg) / segCount;

const esgSegments = computed(() => {
  const valueAngle = angleFor(esgValueAnimated.value);
  return Array.from({ length: segCount }, (_, i) => {
    const a0 = esgStartDeg + i * segSpan;
    const a1 = a0 + segSpan * (1 - segGap);
    const mid = a0 + segSpan / 2;
    return { d: ringSector(outerR, innerR, a0, a1), side: i < segCount / 2 ? "left" : "right", active: mid <= valueAngle };
  });
});

const esgNeedleAngle = computed(() => angleFor(esgValueAnimated.value));
const trackPath = computed(() => arcPath(trackR, esgStartDeg, esgEndDeg));
const progressPath = computed(() => arcPath(trackR, esgStartDeg, esgNeedleAngle.value));

function pointOnArc(r, deg) {
  return { x: r * Math.cos(toRad(deg)), y: r * Math.sin(toRad(deg)) };
}

const labelRadius = outerR + 12;
const label0 = computed(() => pointOnArc(labelRadius, esgStartDeg));
const label160 = computed(() => pointOnArc(labelRadius, esgEndDeg));
const midAngle = (esgStartDeg + esgEndDeg) / 2;
const labelMid = computed(() => pointOnArc(labelRadius + 4, midAngle));
const captionPos = computed(() => ({ x: 0, y: trackR + 0 }));

function svgToScreen(svg, x, y) {
  const pt = svg.createSVGPoint();
  pt.x = x;
  pt.y = y;
  const m = svg.getScreenCTM();
  const s = pt.matrixTransform(m);
  return { x: s.x, y: s.y };
}

function startDrag(e) {
  dragging = true;
  updateFromPointer(e);
}

function onDrag(e) {
  if (dragging) updateFromPointer(e);
}

function stopDrag() {
  dragging = false;
}

function updateFromPointer(e) {
  const svg = gaugeSvg.value;
  if (!svg) return;
  const c = svgToScreen(svg, 0, 0);
  const p = "touches" in e ? e.touches[0] : e;
  const dx = p.clientX - c.x;
  const dy = p.clientY - c.y;
  let ang = (Math.atan2(dy, dx) * 180) / Math.PI;
  ang = Math.max(esgStartDeg, Math.min(esgEndDeg, ang));
  const val = esgGaugeMin + ((ang - esgStartDeg) * (esgGaugeMax - esgGaugeMin)) / (esgEndDeg - esgStartDeg);
  esgGaugeValue.value = Math.round(val);
}

// ESG Score Chart
const esgScoreChart = computed(() => ({
  series: [
    safeCurrentData.value.esg.spaceAllocation.lobby,
    safeCurrentData.value.esg.spaceAllocation.conferenceRoom,
    safeCurrentData.value.esg.spaceAllocation.meetingRoom,
    safeCurrentData.value.esg.spaceAllocation.openOffice
  ],
  options: {
    chart: { type: "donut" },
    colors: ["#3F5BAA", "#54CA54", "#FF9114", "#FDD433"],
    labels: [
      t("dashboards.real-estate-owners.lobbyLabel"),
      t("dashboards.real-estate-owners.conferenceRoomsLabel"),
      t("dashboards.real-estate-owners.meetingRoomLabel"),
      t("dashboards.real-estate-owners.openOffice")
    ],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: { pie: { donut: { size: "75%" } } }
  }
}));

// Occupancy Rate Chart
const occupancyRateChart = ref({
  series: [45, 55],
  options: {
    chart: { type: "donut" },
    colors: ["#3b82f6", ""],
    labels: [t("dashboards.real-estate-owners.occupied"), t("dashboards.real-estate-owners.free")],
    plotOptions: {
      pie: {
        donut: {
          size: "75%",
          labels: {
            show: true,
            value: {
              show: true,
              fontSize: "32px",
              fontWeight: "bold",
              color: "#ffffff",
              formatter: () => "",
            },
            total: {
              show: true,
              label: t("dashboards.real-estate-owners.openOffice"),
              fontSize: "14px",
              color: "#94a3b8",
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
  },
});

// Space Efficiency Chart
const spaceEfficiencyChart = computed(() => ({
  series: [
    safeCurrentData.value.operational.spaceEfficiency.openOffice,
    safeCurrentData.value.operational.spaceEfficiency.meetingRoom,
    safeCurrentData.value.operational.spaceEfficiency.lobby,
    safeCurrentData.value.operational.spaceEfficiency.conferenceRoom
  ],
  options: {
    chart: { type: "donut", background: "transparent" },
    colors: ["#3b82f6", "#10b981", "#f97316", "#eab308"],
    labels: [
      t("dashboards.real-estate-owners.openOffice"),
      t("dashboards.real-estate-owners.meetingRoomLabel"),
      t("dashboards.real-estate-owners.lobbyLabel"),
      t("dashboards.real-estate-owners.conferenceRoomsLabel")
    ],
    plotOptions: {
      pie: {
        donut: {
          size: "75%",
          labels: {
            show: true,
            value: { show: true, fontSize: "36px", fontWeight: "bold", color: "#ffffff", formatter: () => "" },
            total: { show: true, label: t("dashboards.real-estate-owners.openOffice"), fontSize: "20px", color: "#94a3b8", formatter: () => "" }
          }
        }
      }
    },
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { show: false }
  }
}));

// Lifecycle
onMounted(() => {
  initializeData();
  if (activeKpiId.value === 3 && isDataLoaded.value) {
    esgGaugeValue.value = 0;
    esgValueAnimated.value = 0;
    setTimeout(() => { esgGaugeValue.value = safeCurrentData.value.esg.overallScore; }, 250);
  }
});

watch(activeKpiId, async (id) => {
  if (id === 3 && isDataLoaded.value) {
    esgGaugeValue.value = 0;
    esgValueAnimated.value = 0;
    await nextTick();
    setTimeout(() => { esgGaugeValue.value = safeCurrentData.value.esg.overallScore; }, 250);
  }
});
</script>

<style scoped>
@import "@/components/OneVrAnalytics/AnalyticsDashboardStyles/RealEstateOwners.css";
</style>